// Conservative starting values from recent completed Views. The server supplies
// recent timings for the current models as soon as a durable job starts.
export const VIEW_COMPOSE_PHASES = ['selection', 'composition', 'illustration'];
export const DEFAULT_VIEW_TIMINGS = { selection: [15000], composition: [50000], illustration: [8000] };
export const VIEW_COMPOSE_ESTIMATE_MS = 73000;

export function timingQuantile(values, fraction = .75) {
  const sorted = values.filter(value => Number.isFinite(value) && value > 0).sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * fraction))] || 0;
}

export function viewComposeProgress(startedAt, now = Date.now(), live = null) {
  const elapsed = Math.max(0, now - (Number(startedAt) || now));
  const phaseIndex = VIEW_COMPOSE_PHASES.indexOf(live?.phase);
  if (phaseIndex < 0) live = null;
  const phase = live?.phase || 'selection';
  const sinceUpdate = live ? Math.max(0, now - (Number(live.receivedAt) || now)) : 0;
  const phaseElapsed = live ? Math.max(0, (Number(live.phaseElapsedMs) || 0) + sinceUpdate) : elapsed;
  const timings = Object.fromEntries(VIEW_COMPOSE_PHASES.map(key => [key,
    Array.isArray(live?.timings?.[key]) && timingQuantile(live.timings[key]) ? live.timings[key] : DEFAULT_VIEW_TIMINGS[key],
  ]));
  const estimates = VIEW_COMPOSE_PHASES.map(key => timingQuantile(timings[key]));
  // Only the server advances the stage. A slow composition cannot appear to be
  // generating an illustration merely because a timer has run out.
  const index = Math.max(0, phaseIndex);
  const expectedPhase = estimates[index];
  const future = estimates.slice(index + 1).reduce((sum, ms) => sum + ms, 0);
  const remaining = live ? expectedPhase - phaseElapsed + future : VIEW_COMPOSE_ESTIMATE_MS - elapsed;
  const overdue = live ? phaseElapsed >= expectedPhase : elapsed >= VIEW_COMPOSE_ESTIMATE_MS;
  const total = estimates.reduce((sum, ms) => sum + ms, 0);
  const complete = estimates.slice(0, index).reduce((sum, ms) => sum + ms, 0);
  const stageProgress = .9 * (1 - Math.exp(-phaseElapsed / Math.max(1, expectedPhase * .6)));
  const progress = live ? Math.min(.97, (complete + expectedPhase * stageProgress) / total)
    : Math.min(.65, elapsed / VIEW_COMPOSE_ESTIMATE_MS * .65);
  let timeLabel = 'Taking longer than usual';
  if (!overdue) {
    const seconds = Math.max(5, Math.ceil(remaining / 5000) * 5);
    timeLabel = seconds >= 120 ? `About ${Math.ceil(seconds / 60)} minutes left` : `About ${seconds} seconds left`;
  }
  return {
    elapsed, progress, percent: Math.round(progress * 100), timeLabel,
    stage: !live ? 'Preparing your View' : {
      selection: 'Finding the strongest material',
      composition: 'Shaping the reading path',
      illustration: 'Creating the illustration',
    }[phase],
  };
}
